import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';

import {
  AdminQuestionnaireDetail,
  AdminQuestionnaireRow,
  QuestionChoice,
  QuestionGroupRow,
  QuestionRow,
  QuestionType,
  adminQuestionnaireApi,
  UpdateQuestionnairePayload,
} from '../../../../api/adminQuestionnaireApi';
import { paths } from '../../../../routes/paths';
import { colors, primaryButtonSx, secondaryButtonSx, typo } from '../../../../theme/tokens';

import QuestionnaireVersionHistoryDrawer from './QuestionnaireVersionHistoryDrawer';
import PublishConfirmationDialog, {
  PublishEntity,
} from '../tasks/PublishConfirmationDialog';
import { WriteButton, WriteIconButton } from '../../../../components/ui';

const LANGUAGE_OPTIONS = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'es-US', label: 'Spanish (US)' },
  { code: 'fr-CA', label: 'French (CA)' },
];

const QUESTION_TYPE_OPTIONS: { value: QuestionType; label: string }[] = [
  { value: 'TEXT', label: 'Short text' },
  { value: 'LONG_TEXT', label: 'Long text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'SINGLE_CHOICE', label: 'Single choice' },
  { value: 'MULTI_CHOICE', label: 'Multi choice' },
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'DATE', label: 'Date' },
];

function isChoiceType(t: QuestionType): boolean {
  return t === 'SINGLE_CHOICE' || t === 'MULTI_CHOICE';
}

interface ParentFormState {
  questionnaire_name: string;
  language_code: string;
  active: boolean;
}

function parentFormFromDetail(d: AdminQuestionnaireDetail): ParentFormState {
  return {
    questionnaire_name: d.questionnaire_name ?? '',
    language_code: d.language_code ?? 'en-US',
    active: !!d.active,
  };
}

function statusChipProps(status: AdminQuestionnaireRow['status']): {
  label: string;
  bg: string;
  color: string;
} {
  if (status === 'unpublished')
    return { label: 'Unpublished', bg: colors.warningBgStrong, color: colors.warningText };
  if (status === 'in_sync')
    return { label: 'In sync', bg: colors.successBgStrong, color: colors.successText };
  return { label: 'Never published', bg: colors.bgMuted, color: colors.textSecondary };
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const SectionLabel: React.FC<{ label: string; hint?: string }> = ({ label, hint }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 700,
        fontFamily: 'Manrope, sans-serif',
        color: colors.adminAccent,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {label}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: 12, color: colors.textTertiary, mt: 0.25 }}>{hint}</Typography>
    )}
  </Box>
);

/* ================================================================== */
/*  Choices editor (for SINGLE_CHOICE / MULTI_CHOICE questions)        */
/* ================================================================== */

const ChoicesEditor: React.FC<{
  value: QuestionChoice[];
  disabled?: boolean;
  onChange: (next: QuestionChoice[]) => void;
}> = ({ value, disabled, onChange }) => {
  const updateAt = (idx: number, patch: Partial<QuestionChoice>) => {
    const next = value.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onChange(next);
  };
  const removeAt = (idx: number) => onChange(value.filter((_, i) => i !== idx));
  const addChoice = () =>
    onChange([...value, { choice_code: `opt-${value.length + 1}`, choice_label: '' }]);

  const minUnmet = value.length < 2;

  return (
    <Box sx={{ mt: 1, pl: 1, borderLeft: `2px solid ${colors.borderLight}` }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: colors.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: 1,
        }}
      >
        Choices {minUnmet && <Box component="span" sx={{ color: colors.errorText }}>· min 2</Box>}
      </Typography>
      {value.map((c, idx) => (
        <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
          <TextField
            size="small"
            label="code"
            value={c.choice_code}
            onChange={(e) => updateAt(idx, { choice_code: e.target.value })}
            disabled={disabled}
            inputProps={{ maxLength: 50 }}
            InputProps={{
              sx: { fontFamily: 'Fira Code, monospace', fontSize: 12, width: 140 },
            }}
          />
          <TextField
            size="small"
            label="label"
            value={c.choice_label}
            onChange={(e) => updateAt(idx, { choice_label: e.target.value })}
            disabled={disabled}
            inputProps={{ maxLength: 120 }}
            sx={{ flex: 1 }}
          />
          <Tooltip title="Remove choice">
            <span>
              <WriteIconButton
                size="small"
                disabled={disabled || value.length <= 2}
                onClick={() => removeAt(idx)}
              >
                <DeleteIcon fontSize="small" sx={{ color: colors.errorIcon }} />
              </WriteIconButton>
            </span>
          </Tooltip>
        </Box>
      ))}
      <WriteButton
        size="small"
        startIcon={<AddIcon />}
        disabled={disabled}
        onClick={addChoice}
        sx={{ textTransform: 'none', color: colors.adminAccent, fontWeight: 600, mt: 0.5 }}
      >
        Add choice
      </WriteButton>
    </Box>
  );
};

/* ================================================================== */
/*  Question row                                                        */
/* ================================================================== */

const QuestionEditor: React.FC<{
  questionnaireCode: string;
  groupId: number;
  question: QuestionRow;
  disabled: boolean;
  onChanged: () => void;
  onError: (msg: string) => void;
}> = ({ questionnaireCode, groupId, question, disabled, onChanged, onError }) => {
  const [local, setLocal] = useState<QuestionRow>(question);
  const [pendingDelete, setPendingDelete] = useState(false);

  useEffect(() => {
    setLocal(question);
  }, [question]);

  const updateMutation = useMutation({
    mutationFn: (patch: Partial<QuestionRow>) =>
      adminQuestionnaireApi.updateQuestion(
        questionnaireCode,
        groupId,
        question.question_id,
        {
          question_text: patch.question_text,
          question_type: patch.question_type,
          required: patch.required,
          display_order: patch.display_order,
          choices: patch.choices,
        },
      ),
    onSuccess: () => onChanged(),
    onError: () => onError('Question save failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      adminQuestionnaireApi.deleteQuestion(questionnaireCode, groupId, question.question_id),
    onSuccess: () => {
      setPendingDelete(false);
      onChanged();
    },
    onError: () => {
      setPendingDelete(false);
      onError('Question delete failed');
    },
  });

  const commitUpdate = (patch: Partial<QuestionRow>) => {
    if (disabled) return;
    const nextLocal = { ...local, ...patch };
    setLocal(nextLocal);
    updateMutation.mutate(patch);
  };

  const choiceInvalid =
    isChoiceType(local.question_type) && (!local.choices || local.choices.length < 2);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1.5,
        borderColor: colors.borderLight,
        borderRadius: '8px',
        backgroundColor: colors.bgWhite,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <Box
          component="code"
          sx={{
            ...typo.mono,
            fontSize: 11,
            fontWeight: 700,
            color: colors.adminAccent,
            backgroundColor: colors.adminAccentSubtle,
            borderRadius: '4px',
            px: 0.75,
            py: 0.25,
            flexShrink: 0,
          }}
        >
          {local.question_code}
        </Box>
        <TextField
          size="small"
          type="number"
          label="order"
          value={local.display_order}
          onChange={(e) => setLocal({ ...local, display_order: Number(e.target.value) })}
          onBlur={() =>
            local.display_order !== question.display_order &&
            commitUpdate({ display_order: local.display_order })
          }
          disabled={disabled}
          inputProps={{ style: { width: 52 }, min: 0, step: 1 }}
          sx={{ flexShrink: 0 }}
        />
        <Tooltip title="Delete question">
          <span>
            <WriteIconButton
              size="small"
              disabled={disabled || deleteMutation.isPending}
              onClick={() => setPendingDelete(true)}
              sx={{ ml: 'auto' }}
            >
              <DeleteIcon fontSize="small" sx={{ color: colors.errorIcon }} />
            </WriteIconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr auto' } }}>
        <TextField
          size="small"
          label="question_text"
          value={local.question_text}
          onChange={(e) => setLocal({ ...local, question_text: e.target.value })}
          onBlur={() =>
            local.question_text !== question.question_text &&
            commitUpdate({ question_text: local.question_text })
          }
          disabled={disabled}
          inputProps={{ maxLength: 500 }}
          multiline
          maxRows={3}
        />
        <TextField
          size="small"
          select
          label="question_type"
          value={local.question_type}
          onChange={(e) => {
            const next = e.target.value as QuestionType;
            const ensuredChoices = isChoiceType(next)
              ? local.choices && local.choices.length >= 2
                ? local.choices
                : [
                    { choice_code: 'opt-1', choice_label: '' },
                    { choice_code: 'opt-2', choice_label: '' },
                  ]
              : null;
            setLocal({ ...local, question_type: next, choices: ensuredChoices });
            commitUpdate({ question_type: next, choices: ensuredChoices });
          }}
          disabled={disabled}
        >
          {QUESTION_TYPE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Switch
            size="small"
            checked={local.required}
            disabled={disabled}
            onChange={(e) => {
              const next = e.target.checked;
              setLocal({ ...local, required: next });
              commitUpdate({ required: next });
            }}
          />
          <Typography sx={{ fontSize: 12 }}>Required</Typography>
        </Box>
      </Box>

      {isChoiceType(local.question_type) && (
        <ChoicesEditor
          value={local.choices ?? []}
          disabled={disabled}
          onChange={(next) => {
            setLocal({ ...local, choices: next });
            commitUpdate({ choices: next });
          }}
        />
      )}

      {choiceInvalid && (
        <Typography sx={{ fontSize: 11, color: colors.errorText, mt: 1 }}>
          Choice-type questions require at least 2 choices.
        </Typography>
      )}

      <Dialog
        open={pendingDelete}
        onClose={() => !deleteMutation.isPending && setPendingDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
          Delete question{' '}
          <Box component="code" sx={{ ...typo.mono, fontSize: 13 }}>
            {question.question_code}
          </Box>
          ?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
            This mutates the draft questionnaire immediately. Publish to roll out to tenants.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPendingDelete(false)} sx={secondaryButtonSx}>
            Cancel
          </Button>
          <WriteButton
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </WriteButton>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

/* ================================================================== */
/*  Group accordion                                                     */
/* ================================================================== */

const GroupAccordion: React.FC<{
  questionnaireCode: string;
  group: QuestionGroupRow;
  disabled: boolean;
  onChanged: () => void;
  onError: (msg: string) => void;
}> = ({ questionnaireCode, group, disabled, onChanged, onError }) => {
  const [local, setLocal] = useState<{ group_name: string; display_order: number }>({
    group_name: group.group_name,
    display_order: group.display_order,
  });
  const [pendingDelete, setPendingDelete] = useState(false);
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    question_code: '',
    question_text: '',
    question_type: 'TEXT' as QuestionType,
    required: false,
  });

  useEffect(() => {
    setLocal({ group_name: group.group_name, display_order: group.display_order });
  }, [group.group_name, group.display_order]);

  const updateMutation = useMutation({
    mutationFn: (patch: { group_name?: string; display_order?: number }) =>
      adminQuestionnaireApi.updateGroup(questionnaireCode, group.group_id, patch),
    onSuccess: () => onChanged(),
    onError: () => onError('Group save failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => adminQuestionnaireApi.deleteGroup(questionnaireCode, group.group_id),
    onSuccess: () => {
      setPendingDelete(false);
      onChanged();
    },
    onError: () => {
      setPendingDelete(false);
      onError('Group delete failed');
    },
  });

  const addQuestionMutation = useMutation({
    mutationFn: () => {
      const ensuredChoices = isChoiceType(newQuestion.question_type)
        ? [
            { choice_code: 'opt-1', choice_label: '' },
            { choice_code: 'opt-2', choice_label: '' },
          ]
        : null;
      return adminQuestionnaireApi.createQuestion(questionnaireCode, group.group_id, {
        question_code: newQuestion.question_code.trim(),
        question_text: newQuestion.question_text.trim(),
        question_type: newQuestion.question_type,
        required: newQuestion.required,
        display_order: group.questions.length + 1,
        choices: ensuredChoices,
      });
    },
    onSuccess: () => {
      setAddingQuestion(false);
      setNewQuestion({
        question_code: '',
        question_text: '',
        question_type: 'TEXT',
        required: false,
      });
      onChanged();
    },
    onError: () => onError('Question create failed'),
  });

  return (
    <Accordion
      defaultExpanded
      disableGutters
      sx={{
        mb: 1,
        border: `1px solid ${colors.borderLight}`,
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
        backgroundColor: colors.bgWhite,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Box
            component="code"
            sx={{
              ...typo.mono,
              fontSize: 12,
              fontWeight: 700,
              color: colors.adminAccent,
              backgroundColor: colors.adminAccentSubtle,
              borderRadius: '4px',
              px: 0.75,
              py: 0.25,
            }}
          >
            {group.group_code}
          </Box>
          <Typography sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif', fontSize: 14 }}>
            {group.group_name || '(untitled group)'}
          </Typography>
          <Chip
            size="small"
            label={`${group.questions.length} ${group.questions.length === 1 ? 'question' : 'questions'}`}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              backgroundColor: colors.bgMuted,
              color: colors.textSecondary,
            }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr auto' }, mb: 2 }}>
          <TextField
            size="small"
            label="group_name"
            value={local.group_name}
            onChange={(e) => setLocal({ ...local, group_name: e.target.value })}
            onBlur={() =>
              local.group_name !== group.group_name &&
              updateMutation.mutate({ group_name: local.group_name })
            }
            disabled={disabled}
            inputProps={{ maxLength: 120 }}
          />
          <TextField
            size="small"
            type="number"
            label="display_order"
            value={local.display_order}
            onChange={(e) => setLocal({ ...local, display_order: Number(e.target.value) })}
            onBlur={() =>
              local.display_order !== group.display_order &&
              updateMutation.mutate({ display_order: local.display_order })
            }
            disabled={disabled}
            inputProps={{ min: 0, step: 1 }}
          />
          <Tooltip title="Delete group and all questions within it">
            <span>
              <WriteButton
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                disabled={disabled || deleteMutation.isPending}
                onClick={() => setPendingDelete(true)}
                sx={{ textTransform: 'none' }}
              >
                Delete group
              </WriteButton>
            </span>
          </Tooltip>
        </Box>

        {group.questions.length === 0 && !addingQuestion && (
          <Typography
            sx={{
              ...typo.secondaryText,
              textAlign: 'center',
              fontStyle: 'italic',
              py: 2,
            }}
          >
            No questions yet.
          </Typography>
        )}

        {group.questions.map((q) => (
          <QuestionEditor
            key={q.question_id}
            questionnaireCode={questionnaireCode}
            groupId={group.group_id}
            question={q}
            disabled={disabled}
            onChanged={onChanged}
            onError={onError}
          />
        ))}

        {addingQuestion && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 1.5,
              borderColor: colors.adminAccent,
              borderRadius: '8px',
              backgroundColor: colors.adminAccentSubtle,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: colors.adminAccent,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1.5,
              }}
            >
              New question
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 2fr 1fr auto' } }}>
              <TextField
                size="small"
                label="question_code"
                value={newQuestion.question_code}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, question_code: e.target.value })
                }
                inputProps={{ maxLength: 50 }}
                InputProps={{
                  sx: { fontFamily: 'Fira Code, monospace', fontSize: 12 },
                }}
              />
              <TextField
                size="small"
                label="question_text"
                value={newQuestion.question_text}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, question_text: e.target.value })
                }
                inputProps={{ maxLength: 500 }}
              />
              <TextField
                size="small"
                select
                label="type"
                value={newQuestion.question_type}
                onChange={(e) =>
                  setNewQuestion({
                    ...newQuestion,
                    question_type: e.target.value as QuestionType,
                  })
                }
              >
                {QUESTION_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Switch
                  size="small"
                  checked={newQuestion.required}
                  onChange={(e) =>
                    setNewQuestion({ ...newQuestion, required: e.target.checked })
                  }
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
              <Button
                size="small"
                onClick={() => {
                  setAddingQuestion(false);
                  setNewQuestion({
                    question_code: '',
                    question_text: '',
                    question_type: 'TEXT',
                    required: false,
                  });
                }}
                sx={secondaryButtonSx}
              >
                Cancel
              </Button>
              <WriteButton
                size="small"
                variant="contained"
                disabled={
                  !newQuestion.question_code.trim() ||
                  !newQuestion.question_text.trim() ||
                  addQuestionMutation.isPending
                }
                onClick={() => addQuestionMutation.mutate()}
                sx={{
                  ...primaryButtonSx,
                  backgroundColor: colors.adminAccent,
                  color: colors.bgWhite,
                  '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
                }}
              >
                {addQuestionMutation.isPending ? 'Adding…' : 'Add question'}
              </WriteButton>
            </Box>
          </Paper>
        )}

        {!addingQuestion && (
          <WriteButton
            startIcon={<AddIcon />}
            size="small"
            disabled={disabled}
            onClick={() => setAddingQuestion(true)}
            sx={{
              textTransform: 'none',
              color: colors.adminAccent,
              fontWeight: 600,
            }}
          >
            Add question
          </WriteButton>
        )}
      </AccordionDetails>

      <Dialog
        open={pendingDelete}
        onClose={() => !deleteMutation.isPending && setPendingDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
          Delete group{' '}
          <Box component="code" sx={{ ...typo.mono, fontSize: 13 }}>
            {group.group_code}
          </Box>
          ?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
            Deletes the group and all {group.questions.length} question
            {group.questions.length === 1 ? '' : 's'} it contains. Publish to roll out to
            tenants.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPendingDelete(false)} sx={secondaryButtonSx}>
            Cancel
          </Button>
          <WriteButton
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete group'}
          </WriteButton>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
};

/* ================================================================== */
/*  Main edit page                                                     */
/* ================================================================== */

const QuestionnaireEdit: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<ParentFormState | null>(null);
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versionConflict, setVersionConflict] = useState<number | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ group_code: '', group_name: '' });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error' | 'info';
  }>({ open: false, msg: '', sev: 'success' });
  const initialFormRef = useRef<ParentFormState | null>(null);

  const detailQuery = useQuery<AdminQuestionnaireDetail>({
    queryKey: ['admin', 'questionnaires', code],
    queryFn: () => adminQuestionnaireApi.get(code),
    enabled: !!code,
  });

  useEffect(() => {
    if (!detailQuery.data) return;
    const next = parentFormFromDetail(detailQuery.data);
    setForm(next);
    initialFormRef.current = next;
    setBaseVersion(detailQuery.data.current_version);
  }, [detailQuery.data]);

  const isDirty = useMemo(() => {
    if (!form || !initialFormRef.current) return false;
    const a = form;
    const b = initialFormRef.current;
    return (
      a.questionnaire_name !== b.questionnaire_name ||
      a.language_code !== b.language_code ||
      a.active !== b.active
    );
  }, [form]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!form || baseVersion == null) throw new Error('No form state');
      const payload: UpdateQuestionnairePayload = {
        base_version: baseVersion,
        questionnaire_name: form.questionnaire_name,
        language_code: form.language_code,
      };
      return adminQuestionnaireApi.update(code, payload);
    },
    onSuccess: (row) => {
      setVersionConflict(null);
      setEditMode(false);
      setSnackbar({ open: true, msg: `Saved v${row.current_version}`, sev: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'questionnaires', code] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'questionnaires', 'list'] });
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && data?.error === 'version_conflict') {
        setVersionConflict(data.current_version);
      } else {
        setSnackbar({
          open: true,
          msg: err?.message || 'Save failed — try again.',
          sev: 'error',
        });
      }
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async () => {
      if (baseVersion == null) throw new Error('No base version');
      return adminQuestionnaireApi.update(code, { base_version: baseVersion, active: true });
    },
    onSuccess: (row) => {
      setReactivateOpen(false);
      setSnackbar({ open: true, msg: `Reactivated ${row.questionnaire_code}`, sev: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'questionnaires', code] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'questionnaires', 'list'] });
    },
    onError: () => {
      setSnackbar({ open: true, msg: 'Reactivate failed — try again.', sev: 'error' });
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: () => {
      const nextOrder = (detailQuery.data?.groups?.length ?? 0) + 1;
      return adminQuestionnaireApi.createGroup(code, {
        group_code: newGroup.group_code.trim(),
        group_name: newGroup.group_name.trim(),
        display_order: nextOrder,
      });
    },
    onSuccess: () => {
      setAddingGroup(false);
      setNewGroup({ group_code: '', group_name: '' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'questionnaires', code] });
    },
    onError: () =>
      setSnackbar({ open: true, msg: 'Group create failed', sev: 'error' }),
  });

  const handleSubChanged = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'questionnaires', code] });
  }, [queryClient, code]);

  const handleSubError = useCallback((msg: string) => {
    setSnackbar({ open: true, msg, sev: 'error' });
  }, []);

  const handleCancelEdit = useCallback(() => {
    if (isDirty && !window.confirm('Discard unsaved changes?')) return;
    if (initialFormRef.current) setForm(initialFormRef.current);
    setEditMode(false);
    setVersionConflict(null);
  }, [isDirty]);

  const handleReload = useCallback(async () => {
    if (isDirty && !window.confirm('Discard local changes and reload the latest version?')) return;
    setVersionConflict(null);
    await queryClient.invalidateQueries({ queryKey: ['admin', 'questionnaires', code] });
    setEditMode(false);
  }, [isDirty, queryClient, code]);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const tag = (ev.target as HTMLElement | null)?.tagName;
      const isInputTarget = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      const cmd = ev.metaKey || ev.ctrlKey;

      if (cmd && ev.key.toLowerCase() === 's') {
        ev.preventDefault();
        if (editMode && isDirty && !saveMutation.isPending) saveMutation.mutate();
        return;
      }
      if (!isInputTarget) {
        if (ev.key === 'e' && !editMode) {
          ev.preventDefault();
          setEditMode(true);
        } else if (ev.key === 'v') {
          ev.preventDefault();
          setHistoryOpen(true);
        } else if (ev.key === 'Escape' && editMode) {
          handleCancelEdit();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editMode, isDirty, saveMutation, handleCancelEdit]);

  if (detailQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.adminAccent }} />
      </Box>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Alert
        severity="error"
        action={
          <Button
            size="small"
            onClick={() => navigate(paths.admin.globalCatalog.questionnaires())}
            sx={{ color: colors.errorText }}
          >
            Back to list
          </Button>
        }
      >
        Couldn&apos;t load questionnaire <code>{code}</code>.
      </Alert>
    );
  }

  const detail = detailQuery.data;
  const cp = statusChipProps(detail.status);
  const platformVersion =
    detail.platform_questionnaire_id == null ? null : detail.current_version;
  const hasUnpublished = platformVersion != null && detail.current_version > platformVersion;
  const isInactive = !detail.active;
  const groups = detail.groups ?? [];

  return (
    <Box sx={{ maxWidth: 1200, pb: editMode ? 10 : 4 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: colors.textSecondary,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          mb: 1.5,
        }}
      >
        <MuiLink
          onClick={() => navigate(paths.admin.home())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mr: 0.5 }}
        >
          Admin
        </MuiLink>
        /
        <MuiLink
          onClick={() => navigate(paths.admin.globalCatalog.root())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mx: 0.5 }}
        >
          Global Catalog
        </MuiLink>
        /
        <MuiLink
          onClick={() => navigate(paths.admin.globalCatalog.questionnaires())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mx: 0.5 }}
        >
          Questionnaires
        </MuiLink>
        /
        <span style={{ marginLeft: 4, fontFamily: 'Fira Code, monospace', color: colors.textPrimary }}>
          {detail.questionnaire_code}
        </span>
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 1.5,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              ...typo.pageTitle,
              fontSize: 32,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {detail.questionnaire_name || detail.questionnaire_code}
          </Typography>
          <Chip
            label={detail.language_code}
            size="small"
            sx={{
              fontFamily: 'Fira Code, monospace',
              fontSize: 12,
              backgroundColor: colors.adminAccentSubtle,
              color: colors.adminAccent,
              fontWeight: 600,
            }}
          />
          <Chip
            label={cp.label}
            size="small"
            sx={{
              fontSize: 12,
              fontWeight: 700,
              backgroundColor: cp.bg,
              color: cp.color,
              borderRadius: '6px',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setHistoryOpen(true)}
            sx={secondaryButtonSx}
          >
            History <Box component="span" sx={{ fontSize: 11, opacity: 0.6, ml: 0.5 }}>(v)</Box>
          </Button>
          {!editMode && !isInactive && (
            <WriteButton
              size="small"
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditMode(true)}
              sx={primaryButtonSx}
            >
              Edit <Box component="span" sx={{ fontSize: 11, opacity: 0.7, ml: 0.5 }}>(e)</Box>
            </WriteButton>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 3,
          ...typo.smallText,
          color: hasUnpublished ? colors.warningText : colors.textSecondary,
        }}
      >
        <span>Current version: v{detail.current_version}</span>
        <span style={{ color: colors.borderDefault }}>·</span>
        <span>
          {platformVersion != null
            ? `Last published: v${platformVersion} on ${formatDate(detail.updated_at)} by ${detail.updated_by ?? '—'}`
            : 'Never published'}
        </span>
        <span style={{ color: colors.borderDefault }}>·</span>
        <Tooltip title="Tenant usage scan lands in a later round">
          <Button
            size="small"
            startIcon={<GroupIcon sx={{ fontSize: 14 }} />}
            onClick={() =>
              setSnackbar({ open: true, msg: 'Tenant usage scan lands in a later round', sev: 'info' })
            }
            sx={{
              textTransform: 'none',
              fontSize: 12,
              fontWeight: 600,
              color: 'inherit',
              p: 0,
              minWidth: 0,
              '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
            }}
          >
            Used by 0 tenants
          </Button>
        </Tooltip>
      </Box>

      {!detail.active && (
        <Alert
          severity="info"
          sx={{ mb: 3, borderRadius: '8px' }}
          action={
            <WriteButton
              size="small"
              onClick={() => setReactivateOpen(true)}
              sx={{ fontWeight: 700, textTransform: 'none' }}
            >
              Reactivate
            </WriteButton>
          }
        >
          This questionnaire is inactive. It&apos;s hidden from tenants and cannot be edited.
        </Alert>
      )}

      {hasUnpublished && detail.active && (
        <Paper
          role="status"
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 4,
            mb: 3,
            p: 2,
            borderRadius: '8px',
            backgroundColor: colors.warningBg,
            borderLeft: `2px solid ${colors.warningIcon}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <WarningAmberIcon sx={{ color: colors.warningIcon, fontSize: 20 }} />
          <Typography sx={{ fontSize: 13, color: colors.warningText, flex: 1 }}>
            <strong>Unpublished changes.</strong> Tenants are still seeing v{platformVersion}.
            Publish this questionnaire to roll it out.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <WriteButton
              variant="contained"
              size="small"
              sx={{
                ...primaryButtonSx,
                backgroundColor: colors.adminAccent,
                color: colors.bgWhite,
                '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
              }}
              onClick={() => setPublishOpen(true)}
            >
              Publish this entity
            </WriteButton>
          </Box>
        </Paper>
      )}

      {versionConflict != null && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: '8px' }}
          action={
            <Button size="small" onClick={handleReload}>
              Reload
            </Button>
          }
        >
          Someone else saved v{versionConflict} while you were editing.
        </Alert>
      )}

      {form && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            border: `1px solid ${colors.borderLight}`,
            backgroundColor: colors.bgWhite,
            p: 3,
            mb: 3,
          }}
        >
          <SectionLabel label="Identity" hint="Code is immutable after creation." />
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            }}
          >
            <TextField
              size="small"
              label="questionnaire_code"
              value={detail.questionnaire_code}
              disabled
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <LockIcon sx={{ fontSize: 16, color: colors.textTertiary, mr: 1 }} />
                ),
                sx: { fontFamily: 'Fira Code, monospace', fontSize: 13 },
              }}
            />
            <TextField
              size="small"
              label="questionnaire_name"
              value={form.questionnaire_name}
              onChange={(e) => setForm({ ...form, questionnaire_name: e.target.value })}
              disabled={!editMode}
              inputProps={{ maxLength: 120 }}
              helperText={editMode ? `${form.questionnaire_name.length} / 120` : undefined}
            />
            <TextField
              size="small"
              select
              SelectProps={{ native: true }}
              label="language_code"
              value={form.language_code}
              disabled={!editMode}
              onChange={(e) => setForm({ ...form, language_code: e.target.value })}
            >
              {LANGUAGE_OPTIONS.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label} ({l.code})
                </option>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={form.active}
                disabled={!editMode}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              <Typography sx={{ fontSize: 13 }}>Active</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Groups + questions — mutate in place (no edit-mode gating on the
          parent form required; nested edits go straight to the server). */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${colors.borderLight}`,
          backgroundColor: colors.bgWhite,
          p: 3,
          mb: 3,
        }}
      >
        <SectionLabel
          label="Question groups"
          hint="Groups + questions mutate in place — no edit-mode required. Publish the parent to roll changes out to tenants."
        />

        {groups.length === 0 && !addingGroup && (
          <Typography sx={{ ...typo.secondaryText, textAlign: 'center', fontStyle: 'italic', py: 4 }}>
            No groups yet. Add one to start collecting questions.
          </Typography>
        )}

        {[...groups]
          .sort((a, b) => a.display_order - b.display_order)
          .map((g) => (
            <GroupAccordion
              key={g.group_id}
              questionnaireCode={detail.questionnaire_code}
              group={g}
              disabled={isInactive}
              onChanged={handleSubChanged}
              onError={handleSubError}
            />
          ))}

        {addingGroup && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 1.5,
              borderColor: colors.adminAccent,
              borderRadius: '8px',
              backgroundColor: colors.adminAccentSubtle,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: colors.adminAccent,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1.5,
              }}
            >
              New group
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' } }}>
              <TextField
                size="small"
                label="group_code"
                value={newGroup.group_code}
                onChange={(e) => setNewGroup({ ...newGroup, group_code: e.target.value })}
                inputProps={{ maxLength: 50 }}
                InputProps={{ sx: { fontFamily: 'Fira Code, monospace', fontSize: 12 } }}
              />
              <TextField
                size="small"
                label="group_name"
                value={newGroup.group_name}
                onChange={(e) => setNewGroup({ ...newGroup, group_name: e.target.value })}
                inputProps={{ maxLength: 120 }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
              <Button
                size="small"
                onClick={() => {
                  setAddingGroup(false);
                  setNewGroup({ group_code: '', group_name: '' });
                }}
                sx={secondaryButtonSx}
              >
                Cancel
              </Button>
              <WriteButton
                size="small"
                variant="contained"
                disabled={
                  !newGroup.group_code.trim() ||
                  !newGroup.group_name.trim() ||
                  createGroupMutation.isPending
                }
                onClick={() => createGroupMutation.mutate()}
                sx={{
                  ...primaryButtonSx,
                  backgroundColor: colors.adminAccent,
                  color: colors.bgWhite,
                  '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
                }}
              >
                {createGroupMutation.isPending ? 'Adding…' : 'Add group'}
              </WriteButton>
            </Box>
          </Paper>
        )}

        {!addingGroup && (
          <WriteButton
            startIcon={<AddIcon />}
            onClick={() => setAddingGroup(true)}
            disabled={isInactive}
            sx={{
              textTransform: 'none',
              color: colors.adminAccent,
              fontWeight: 600,
              mt: 1,
            }}
          >
            Add group
          </WriteButton>
        )}
      </Paper>

      {editMode && (
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            height: 64,
            zIndex: 10,
            backgroundColor: colors.bgWhite,
            borderTop: `1px solid ${colors.borderLight}`,
            display: 'flex',
            alignItems: 'center',
            px: 3,
            borderRadius: 0,
            gap: 1.5,
          }}
          role="toolbar"
          aria-label="Save actions"
        >
          <Button onClick={handleCancelEdit} sx={{ ...secondaryButtonSx, border: 'none' }}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          {isDirty && (
            <Typography sx={{ fontSize: 12, color: colors.textTertiary, mr: 1 }}>
              Unsaved changes
            </Typography>
          )}
          <WriteButton
            variant="outlined"
            onClick={() => saveMutation.mutate()}
            disabled={!isDirty || saveMutation.isPending}
            sx={secondaryButtonSx}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save draft'}{' '}
            <Box component="span" sx={{ fontSize: 11, opacity: 0.6, ml: 0.5 }}>
              (⌘S)
            </Box>
          </WriteButton>
          <WriteButton
            variant="contained"
            sx={{
              ...primaryButtonSx,
              backgroundColor: colors.adminAccent,
              color: colors.bgWhite,
              '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
            }}
            disabled={saveMutation.isPending}
            onClick={async () => {
              if (isDirty) {
                await saveMutation.mutateAsync().catch(() => null);
              }
              setPublishOpen(true);
            }}
          >
            Save &amp; Publish
          </WriteButton>
        </Paper>
      )}

      <QuestionnaireVersionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        detail={detail}
      />

      <PublishConfirmationDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        entity="questionnaire"
        entities={[
          {
            entity: 'questionnaire',
            code: detail.questionnaire_code,
            name: detail.questionnaire_name,
            current_version: detail.current_version,
            baseline_version: platformVersion,
            changed_fields: [],
            diff: [],
          } as PublishEntity,
        ]}
        onCompleted={(res) => {
          setPublishOpen(false);
          if (res && res.batch_id) {
            setSnackbar({
              open: true,
              msg: `Published ${detail.questionnaire_code}`,
              sev: 'success',
            });
            queryClient.invalidateQueries({ queryKey: ['admin', 'questionnaires', code] });
            queryClient.invalidateQueries({
              queryKey: ['admin', 'questionnaires', 'list'],
            });
          }
        }}
      />

      <Dialog
        open={reactivateOpen}
        onClose={() => !reactivateMutation.isPending && setReactivateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
          Reactivate{' '}
          <Box component="code" sx={{ ...typo.mono, fontSize: 14 }}>
            {detail.questionnaire_code}
          </Box>
          ?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
            It will become available to tenants on next sync.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setReactivateOpen(false)}
            sx={secondaryButtonSx}
            disabled={reactivateMutation.isPending}
          >
            Cancel
          </Button>
          <WriteButton
            variant="contained"
            onClick={() => reactivateMutation.mutate()}
            disabled={reactivateMutation.isPending}
            sx={{
              ...primaryButtonSx,
              backgroundColor: colors.adminAccent,
              color: colors.bgWhite,
              '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
            }}
          >
            {reactivateMutation.isPending ? 'Reactivating…' : 'Reactivate'}
          </WriteButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.sev}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuestionnaireEdit;
